import { useAppSelector } from '@/store';
import { authSelector } from '@/store/reducers/auth';
import UseScreenSize from '@/hooks/UseScreenSize';
import UserUtils from '@/utils/user';
import React, { createContext, useState, useContext, ReactNode, useEffect } from 'react';
import { EventDispatcherContext, DispatcherEvents } from './eventDispatcherContext';

interface DrawerContextProps {
  isDrawerOpen: boolean;
  isTopicsOpen: boolean;
  isMobileDrawerOpen: boolean;
  isContactUsDrawerOpen: boolean;
  isAddClassDrawerOpen: boolean;
  isEditClassDrawerOpen: boolean;
  isAddNewStudentDrawerOpen: boolean;
  classId: string;
  groupId: string;
  topicId: number;
  openDrawer: () => void;
  openTopicsDrawer: (topicId: number) => void;
  closeDrawer: () => void;
  openAddClass: () => void;
  closeAddClass: () => void;
  openEditClass: (id: string) => void;
  closeEditClass: () => void;
  openAddStudent: (groupId: string) => void;
  closeAddStudent: () => void;
  closeTopicsDrawer: () => void;
  toggleDrawer: () => void;
  openContactUsDrawer: () => void;
  closeContactUsDrawer: () => void;
  openMobileDrawer: () => void;
  closeMobileDrawer: () => void;
  toggleMobileDrawer: () => void;
}

const DrawerContext = createContext<DrawerContextProps | undefined>(undefined);

export const useDrawer = (): DrawerContextProps => {
  const context = useContext(DrawerContext);
  if (!context) {
    throw new Error('useDrawer must be used within a DrawerProvider');
  }
  return context;
};

interface DrawerProviderProps {
  children: ReactNode;
}

export const DrawerProvider: React.FC<DrawerProviderProps> = ({ children }) => {
  const eventDispatcher = useContext(EventDispatcherContext);
  const [isDrawerOpen, setDrawerOpen] = useState(false);
  const [isTopicsOpen, setIsTopicsOpen] = useState(false);
  const [isMobileDrawerOpen, setMobileDrawerOpen] = useState(false);
  const [isContactUsDrawerOpen, setIsContactUsDrawerOpen] = useState(false);
  const [isAddClassDrawerOpen, setIsAddClassDrawerOpen] = useState(false);
  const [isEditClassDrawerOpen, setIsEditClassDrawerOpen] = useState(false);
  const [isAddNewStudentDrawerOpen, setIsAddNewStudentDrawerOpen] = useState(false);
  const [classId, setClassId] = useState('');
  const [groupId, setGroupId] = useState('');

  const [topicId, setTopicId] = useState<number>(0);
  const { loggedIn, user } = useAppSelector(authSelector);
  const { isMobile } = UseScreenSize();
  const isTeacher = UserUtils.isTeacher(user);

  const openDrawer = () => setDrawerOpen(true);
  const openTopicsDrawer = (topicId: number) => {
    setTopicId(topicId);
    setIsTopicsOpen(true);
  };
  const closeTopicsDrawer = () => setIsTopicsOpen(false);
  const closeDrawer = () => setDrawerOpen(false);
  const openContactUsDrawer = () => setIsContactUsDrawerOpen(true);
  const closeContactUsDrawer = () => setIsContactUsDrawerOpen(false);
  const openAddClass = () => setIsAddClassDrawerOpen(true);
  const closeAddClass = () => setIsAddClassDrawerOpen(false);
  const openAddStudent = (groupId: string) => {
    setIsAddNewStudentDrawerOpen(true);
    setGroupId(groupId);
  };
  const closeAddStudent = () => setIsAddNewStudentDrawerOpen(false);
  const openEditClass = (id: string) => {
    setIsEditClassDrawerOpen(true);
    setClassId(id);
  };
  const closeEditClass = () => setIsEditClassDrawerOpen(false);
  const toggleDrawer = () => setDrawerOpen(prevState => !prevState);

  const openMobileDrawer = () => setMobileDrawerOpen(true);
  const closeMobileDrawer = () => setMobileDrawerOpen(false);
  const toggleMobileDrawer = () => setMobileDrawerOpen(prevState => !prevState);

  useEffect(() => {
    closeDrawer();
    closeMobileDrawer();
  }, [loggedIn]);

  useEffect(() => {
    if (
      isDrawerOpen ||
      isTopicsOpen ||
      isMobileDrawerOpen ||
      isContactUsDrawerOpen ||
      isAddClassDrawerOpen ||
      isEditClassDrawerOpen ||
      isAddNewStudentDrawerOpen
    ) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'auto';
    }
  }, [
    isDrawerOpen,
    isTopicsOpen,
    isMobileDrawerOpen,
    isContactUsDrawerOpen,
    isAddClassDrawerOpen,
    isEditClassDrawerOpen,
    isAddNewStudentDrawerOpen,
    isTeacher,
    isMobile,
  ]);

  useEffect(() => {
    eventDispatcher.addListener(DispatcherEvents.USER_DATA_CHANGE, closeTopicsDrawer);

    return () => {
      eventDispatcher.removeListener(DispatcherEvents.USER_DATA_CHANGE, closeTopicsDrawer);
    };
  }, [eventDispatcher]);

  return (
    <DrawerContext.Provider
      value={{
        isDrawerOpen,
        isTopicsOpen,
        isMobileDrawerOpen,
        isContactUsDrawerOpen,
        isAddClassDrawerOpen,
        isEditClassDrawerOpen,
        isAddNewStudentDrawerOpen,
        classId,
        groupId,
        topicId,
        closeAddClass,
        openAddClass,
        closeEditClass,
        openEditClass,
        openDrawer,
        openTopicsDrawer,
        closeTopicsDrawer,
        closeDrawer,
        toggleDrawer,
        openContactUsDrawer,
        closeContactUsDrawer,
        openMobileDrawer,
        closeMobileDrawer,
        toggleMobileDrawer,
        openAddStudent,
        closeAddStudent,
      }}
    >
      {children}
    </DrawerContext.Provider>
  );
};
